import { useEffect, useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Ingredient, LiveOrder } from '../types';
import { DEFAULT_SERVING_OPTIONS } from '../constants';

const groupIngredients = (ingredients: Ingredient[]) => {
  const grouped: { [key: string]: { ingredient: Ingredient; count: number } } = {};
  ingredients.forEach(ing => {
    const key = `${ing.id}_${ing.isRemoved ? 'removed' : 'added'}`;
    if (grouped[key]) grouped[key].count += 1;
    else grouped[key] = { ingredient: ing, count: 1 };
  });
  return Object.values(grouped);
};

import { getTenantId } from '../services/dbService';

export const useIotAutoPrint = () => {
    const { liveOrders, settings, t, language, graphicsSettings, currentUserTenantId, view } = useApp();
    const [processedIds, setProcessedIds] = useState<Set<string>>(new Set());
    const isFirstMount = useRef(true);

    const getShopUrl = () => {
        const tenantId = getTenantId();
        if (tenantId && tenantId !== 'root') {
            return `https://www.ordinasmart.app/?shop=${tenantId}`;
        }
        return `https://www.ordinasmart.app/`;
    };

    useEffect(() => {
        // Stop if not enabled or if we are the rider app
        if (!settings.iotPrinterSettings?.isEnabled || view === 'rider' || view === 'rider-locked') return;
        
        // Check local device override
        if (localStorage.getItem('disable_local_auto_print') === 'true') {
            return;
        }

        // On first mount, just record all existing pending orders as processed so we don't spam print historical ones
        if (isFirstMount.current) {
            const initialIds = new Set<string>();
            liveOrders.forEach(o => initialIds.add(o.id));
            setProcessedIds(initialIds);
            isFirstMount.current = false;
            return;
        }

        const iotSettings = settings.iotPrinterSettings;
        const newOrdersToPrint: LiveOrder[] = [];

        liveOrders.forEach(order => {
            if (order.status === 'pending' && !processedIds.has(order.id)) {
                newOrdersToPrint.push(order);
                setProcessedIds(prev => new Set(prev).add(order.id));
            }
        });

        if (newOrdersToPrint.length > 0) {
            newOrdersToPrint.forEach(order => {
                executeIotPrint(order, iotSettings);
            });
        }

    }, [liveOrders, settings.iotPrinterSettings, view]);

    const executeIotPrint = async (order: LiveOrder, iotSettings: any) => {
        try {
            console.log(`[IoT Auto-Print] Firing for order ${order.id} mode ${iotSettings.mode}`);
            
            if (iotSettings.mode === 'WEBHOOK_JSON' && iotSettings.webhookUrl) {
                await fetch(iotSettings.webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ event: 'NEW_ORDER', order: order })
                });
            } else if (iotSettings.mode === 'EPSON_EPOS' && iotSettings.printerIp) {
                // Future stub for ePOS ESC/XML
                console.log(`[IoT EPOS] Sending to IP ${iotSettings.printerIp}`);
            } 
            
            // Sempre e comunque, esegui la stampa nel browser locale con il layout ottimale 
            // come richiesto per avere la comanda cartacea in ogni modalità selezionata.
            triggerBrowserHiddenPrint(order);
            
        } catch (e) {
            console.error('[IoT Auto-Print Error]', e);
        }
    };

    const triggerBrowserHiddenPrint = (order: LiveOrder) => {
        const paperWidth = settings.printerSettings?.paperWidth || '80mm';
        const timestamp = new Date(order.timestamp);
        
        let headerTitle = '';
        if (order.context === 'DINE_IN') headerTitle = 'TAVOLO';
        else if (order.context === 'TAKE_AWAY') headerTitle = 'ASPORTO';
        else headerTitle = 'CONSEGNA';

        const receiptWidth = paperWidth === '80mm' ? '75mm' : '48mm';

        const receiptContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <style>
                * {
                    box-sizing: border-box;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                @page { 
                    margin: 0 !important; 
                    size: auto; 
                }
                @media print {
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100% !important;
                        height: auto !important;
                        min-height: 0 !important;
                        max-height: none !important;
                        overflow: visible !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        page-break-after: avoid !important;
                        break-after: avoid !important;
                    }
                    .receipt-container {
                        width: 100% !important;
                        height: auto !important;
                        min-height: 0 !important;
                        max-height: none !important;
                        margin: 0 !important;
                        padding: 4px 0 0 0 !important;
                        overflow: hidden !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        page-break-after: avoid !important;
                        break-after: avoid !important;
                    }
                }
                html, body { 
                    margin: 0; 
                    padding: 0; 
                    width: 100%; 
                    background: white; 
                }
                body { 
                    font-family: 'Helvetica', 'Arial', sans-serif; 
                    color: black; 
                    background: white; 
                    width: ${receiptWidth}; 
                    max-width: ${receiptWidth};
                    margin: 0 auto;
                    font-size: 16px; 
                    line-height: 1.4;
                    font-weight: 600;
                }
                .receipt-container {
                    padding: 4px 0 0 0;
                    box-sizing: border-box;
                    width: 100%;
                }
                .header { text-align: center; margin-bottom: 15px; border-bottom: 2px dashed black; padding-bottom: 10px; }
                .title { font-size: 28px; font-weight: 900; text-transform: uppercase; margin-bottom: 5px; display: block; }
                .time { font-size: 14px; margin-bottom: 5px; }
                .context-box { 
                    font-size: 22px; 
                    font-weight: 900; 
                    margin: 10px 0; 
                    border: 3px solid black; 
                    padding: 5px 10px; 
                    display: inline-block; 
                    text-transform: uppercase;
                }
                .customer { font-size: 20px; font-weight: 900; margin-top: 10px; text-transform: uppercase; display:block; }
                .info-text { font-size: 14px; margin-top: 2px; }
                .phone-text { font-size: 18px; font-weight: 900; margin-top: 5px; display: block; }
                
                .section-title {
                    font-size: 18px;
                    font-weight: 900;
                    text-transform: uppercase;
                    border-bottom: 2px solid black;
                    margin-top: 15px;
                    margin-bottom: 10px;
                    padding-bottom: 2px;
                }

                .item { margin-bottom: 12px; border-bottom: 1px dotted #000; padding-bottom: 8px; }
                .item-row { display: flex; justify-content: space-between; align-items: flex-start; font-weight: 900; font-size: 18px; line-height: 1.2; }
                .item-qty { margin-right: 5px; min-width: 25px; }
                .item-name { flex: 1; text-align: left; }
                .item-price { text-align: right; white-space: nowrap; margin-left: 5px; }
                
                .takeaway-tag { 
                    background-color: black; 
                    color: white; 
                    font-weight: 900; 
                    font-size: 16px; 
                    margin: 6px 0; 
                    padding: 4px 8px; 
                    display: inline-block; 
                    text-transform: uppercase;
                    border: 2px solid black;
                }
                
                .ings { margin-top: 4px; font-size: 14px; padding-left: 10px; font-weight: normal; }
                .ing-row { display: block; }
                .ing-rem { text-decoration: line-through; }
                
                .totals { border-top: 2px solid black; margin-top: 10px; padding-top: 5px; font-size: 16px; }
                .total-row { display: flex; justify-content: space-between; margin-bottom: 2px; }
                
                .final-total { 
                    border-top: 3px solid black; 
                    margin-top: 15px; 
                    padding-top: 10px; 
                    text-align: right; 
                    font-size: 36px; 
                    font-weight: 900; 
                }
                .footer { text-align: center; font-size: 12px; margin-top: 20px; font-weight: normal; }
            </style>
        </head>
        <body>
            <div class="receipt-container">
                <div class="header">
                    ${graphicsSettings?.appLogoType === 'IMAGE' && graphicsSettings?.customLogoUrl ? `<img src="${graphicsSettings.customLogoUrl}" style="max-width: 150px; max-height: 100px; margin: 0 auto 10px auto; display: block; object-fit: contain;" />` : ''}
                    <div class="title">${settings.appName}</div>
                    <div class="time">${timestamp.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US')} ${timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                    <div class="context-box">${headerTitle}</div>
                    <div class="info-text" style="font-size:36px; font-weight:900; margin-top:10px; border-bottom: 2px solid black; padding-bottom: 10px; margin-bottom: 10px;">
                        #${order.id.slice(-4).toUpperCase()}
                    </div>
                    ${order.scheduledTime ? `<div class="context-box" style="border-color: black; background: black; color: white;">PRENOTATO: ${order.scheduledTime === 'ASAP' ? 'APPENA APRITE' : order.scheduledTime}</div>` : ''}
                    <div class="info-text" style="font-size:16px;">
                        ${t('receipt_payment')}: <b>${order.paymentMethod === 'CASH' ? 'CONTANTI' : (order.paymentMethod === 'POS' ? 'POS' : 'ONLINE')}</b>
                    </div>
                    <div class="customer" style="font-size:24px;">👤 ${order.customerName}</div>
                    ${order.customerPhone ? `<div class="phone-text">TEL: ${order.customerPhone}</div>` : ''}
                    ${order.context !== 'DINE_IN' && order.deliveryAddress ? `<div class="info-text" style="font-size:16px; margin-top:5px;">${order.deliveryAddress}</div>` : ''}
                    ${order.context === 'DINE_IN' && order.tableNumber ? `<div class="info-text" style="font-size:24px; font-weight:900; margin-top:5px;">TAVOLO: ${order.tableNumber}</div>` : ''}
                    ${order.intercomName ? `<div class="info-text">CITOFONO: ${order.intercomName}</div>` : ''}
                    ${order.customerNote ? `<div class="info-text" style="font-weight:bold; margin-top:5px; padding:5px; border: 1px solid black;">NOTE: ${order.customerNote}</div>` : ''}
                </div>
                
                <div style="margin-bottom: 20px;">
                ${(() => {
                    const itemsByContext: Record<string, any[]> = {
                        'DINE_IN': [],
                        'TAKE_AWAY': [],
                        'DELIVERY': []
                    };

                    order.items.forEach((item: any) => {
                        let ctx = item.context;
                        if (!ctx) {
                            if (item.servingStyle === 'plate' || item.servingStyle === 'tray') ctx = 'DINE_IN';
                            else if (item.servingStyle === 'box' || item.servingStyle === 'bag') ctx = 'TAKE_AWAY';
                            else ctx = order.context;
                        }

                        if (itemsByContext[ctx]) {
                            itemsByContext[ctx].push(item);
                        } else {
                            itemsByContext[order.context].push(item);
                        }
                    });

                    let html = '';
                    const contexts = ['DINE_IN', 'TAKE_AWAY', 'DELIVERY'];
                    
                    contexts.forEach(ctx => {
                        const items = itemsByContext[ctx];
                        if (items.length > 0) {
                            let sectionTitle = '';
                            if (ctx === 'DINE_IN') sectionTitle = 'MANGIARE QUI';
                            else if (ctx === 'TAKE_AWAY') sectionTitle = 'ASPORTO';
                            else if (ctx === 'DELIVERY') sectionTitle = 'DOMICILIO';

                            const hasMixedContexts = contexts.filter(c => itemsByContext[c].length > 0).length > 1;
                            
                            if (hasMixedContexts) {
                                html += `<div class="section-title">${sectionTitle}</div>`;
                            }

                            // Group identical items together
                            const groupedLineItems: any[] = [];
                            items.forEach((item: any) => {
                                const sortedIngs = [...(item.ingredients || [])].sort((a: any, b: any) => a.id.localeCompare(b.id));
                                const serializedIngs = JSON.stringify(sortedIngs.map((i: any) => ({ id: i.id, isRemoved: i.isRemoved })));
                                const key = `${item.product.id}_${item.servingStyle}_${serializedIngs}`;
                                
                                const existing = groupedLineItems.find(g => g.key === key);
                                if (existing) {
                                    existing.quantity += (item.quantity || 1);
                                } else {
                                    groupedLineItems.push({ ...item, quantity: (item.quantity || 1), key });
                                }
                            });

                            html += groupedLineItems.map((item: any) => {
                                const groupedIngs = groupIngredients(item.ingredients);
                                const servingOptions = settings.servingOptions || DEFAULT_SERVING_OPTIONS;
                                const servingOption = servingOptions.find((opt: any) => opt && opt.id === item.servingStyle);
                                const servingLabel = servingOption ? servingOption.label : (item.servingStyle || '');

                                return `
                                <div class="item">
                                    <div class="item-row">
                                        <span class="item-qty">${item.quantity}x</span>
                                        <span class="item-name">${item.product.name}</span>
                                        <span class="item-price">€ ${((item.itemTotal) * (item.quantity || 1)).toFixed(2)}</span>
                                    </div>
                                    ${servingLabel ? `<div class="takeaway-tag">${servingLabel}</div>` : ''}
                                    <div class="ings">
                                        ${groupedIngs.map(g => {
                                            let priceText = '';
                                            if (!g.ingredient.isRemoved && g.ingredient.extraPrice && g.ingredient.extraPrice > 0) {
                                                priceText = ` <span style="font-weight: bold;">(+€${g.ingredient.extraPrice.toFixed(2)})</span>`;
                                            }
                                            return `<div class="ing-row ${g.ingredient.isRemoved ? 'ing-rem' : ''}">${g.ingredient.isRemoved ? t('without') + ' ' : '+ '}${g.ingredient.name} ${g.count > 1 ? `(x${g.count})` : ''}${priceText}</div>`;
                                        }).join('')}
                                    </div>
                                </div>`;
                            }).join('');
                        }
                    });
                    return html;
                })()}
                </div>
                
                <div class="totals">
                    ${order.deliveryFee ? `<div class="total-row"><span>${t('delivery_cost')}</span><span>€ ${order.deliveryFee.toFixed(2)}</span></div>` : ''}
                    ${order.takeAwayFee ? `<div class="total-row"><span>${t('supplement')}</span><span>€ ${order.takeAwayFee.toFixed(2)}</span></div>` : ''}
                    ${order.smallOrderSurcharge ? `<div class="total-row"><span>Suppl. Ordine Minimo</span><span>+€ ${order.smallOrderSurcharge.toFixed(2)}</span></div>` : ''}
                    ${(order as any).surgeFee ? `<div class="total-row" style="color: #d97706;"><span>Maggiorazione Orario/Festivo</span><span>+€ ${((order as any).surgeFee).toFixed(2)}</span></div>` : ''}
                    ${order.deliveryDiscount ? `<div class="total-row"><span>Sconto App</span><span>-€ ${order.deliveryDiscount.toFixed(2)}</span></div>` : ''}
                    ${(order as any).promoQuantityDiscount ? `<div class="total-row"><span>Sconto Promozionale</span><span>-€ ${((order as any).promoQuantityDiscount).toFixed(2)}</span></div>` : ''}
                    ${order.loyaltyDiscount ? `<div class="total-row"><span>${t('loyalty_discount')}</span><span>-€ ${order.loyaltyDiscount.toFixed(2)}</span></div>` : ''}
                    ${order.couponDiscount ? `<div class="total-row"><span>Coupon (${order.couponCode})</span><span>-€ ${order.couponDiscount.toFixed(2)}</span></div>` : ''}
                    ${order.adminDiscount ? `<div class="total-row"><span>${order.hasReceivedBirthdayDiscountThisYear ? 'Sconto Compleanno / Manuale' : 'Sconto Manuale'}</span><span>-€ ${order.adminDiscount.toFixed(2)}</span></div>` : ''}
                </div>

                <div class="final-total">TOTALE: € ${order.total.toFixed(2)}</div>
                ${order.isBirthdayMonth ? `
                <div style="text-align: center; margin: 15px 0; padding: 10px; border: 2px solid #db2777; border-radius: 12px; background-color: #fdf2f8;">
                    <div style="font-size: 20px; font-weight: 900; color: #db2777; margin-bottom: 4px;">🎂 TANTI AUGURI! 🎈</div>
                    <div style="font-size: 14px; color: #000; font-weight: bold;">Buon compleanno! ✨</div>
                </div>
                ` : ''}
                ${order.loyaltyPointsSnapshot !== undefined ? `
                <div style="text-align: center; margin-top: 15px; padding: 10px; border: 2px dashed #000; border-radius: 8px;">
                    <span style="font-size: 14px; display: block; margin-bottom: 5px;">IL TUO SALDO PUNTI</span>
                    <span style="font-size: 24px; font-weight: 900;">${order.loyaltyPointsSnapshot}</span>
                </div>
                ` : ''}
                ${settings.receiptQrCodeEnabled ? `
                <div style="text-align: center; margin-top: 14px; margin-bottom: 2px; page-break-inside: avoid; break-inside: avoid;">
                    <div style="font-size: 15px; font-weight: bold; margin-bottom: 4px;">${settings.receiptQrCodeText || 'Ordina da noi! Sconti, Punti e altro:'}</div>
                    <div style="position: relative; width: 96px; max-width: 96px; height: 96px; max-height: 96px; margin: 0 auto;">
                        <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&ecc=H&data=${encodeURIComponent(getShopUrl())}" alt="QR Code" style="display: block; width: 100%; height: 100%; object-fit: contain;">
                        ${(graphicsSettings?.appLogoType === 'IMAGE' && graphicsSettings?.customLogoUrl) ? `<img src="${graphicsSettings.customLogoUrl}" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 24px; height: 24px; object-fit: contain; background: white; padding: 2px; border-radius: 4px;" />` : ''}
                    </div>
                </div>
                ` : ''}
                
                <script>
                    window.onload = function() { 
                        setTimeout(function() { window.print(); }, 800); 
                    };
                </script>
            </div>
        </body>
        </html>
        `;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.top = '-10000px';
        iframe.style.left = '-10000px';
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        iframe.contentWindow?.document.write(receiptContent);
        iframe.contentWindow?.document.close();
        
        setTimeout(() => {
            if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
            }
        }, 30000);
    };
    
    return null; // Return null if it's a hook just executing effects, though useIotAutoPrint has no return type previously. Let's keep it without return if not needed, but wait it didn't return anything before
};
